import * as React from "react";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { useLinkProps } from "../../hooks/useLinkProps.js";
import { chevronLeft, chevronRight } from "../../icons/index.js";
import {
  KarusellTrack,
  KarusellCard,
  KarusellCardImage,
  KarusellCardBody,
  KarusellCardTitle,
  KarusellCardText,
  KarusellHeading,
  KarusellArrows,
  KarusellArrowButton,
} from "./styles.js";
import type { BackgroundColor } from "../../types/theme.js";

export interface KarusellProps {
  title?: string;
  list?: KarusellItem[];
  showArrows?: boolean;
  backgroundColor?: BackgroundColor;
}

export interface KarusellItem {
  image?: Image;
  title?: string;
  text?: string;
  link?: Link;
}

export const Karusell: React.FC<KarusellProps> = ({ title, list = [], showArrows = false, backgroundColor }) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  function updateScrollState() {
    const track = trackRef.current;
    if (!track) return;
    const left = Math.round(track.scrollLeft);
    setCanScrollLeft(left > 0);
    setCanScrollRight(left + track.clientWidth < track.scrollWidth - 1);
  }

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || !showArrows) return;
    const raf = requestAnimationFrame(updateScrollState);
    track.addEventListener("scroll", updateScrollState, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      track.removeEventListener("scroll", updateScrollState);
    };
  }, [showArrows, list]);

  function scrollBy(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.firstElementChild as HTMLElement | null;
    const cardWidth = firstCard ? firstCard.offsetWidth + parseFloat(getComputedStyle(track).gap || "0") : 280;
    const target = Math.max(0, track.scrollLeft + direction * cardWidth);
    track.scrollTo({ left: target, behavior: "smooth" });
  }

  return (
    <VerticalSpacer>
      <Constrainer>
        {title && (
          <HorizontalSpacer>
            <KarusellHeading>{title}</KarusellHeading>
          </HorizontalSpacer>
        )}
        <KarusellTrack ref={trackRef}>
          {list.map((item, i) => (
            <Card key={i} {...item} backgroundColor={backgroundColor} />
          ))}
        </KarusellTrack>
        {showArrows && (
          <KarusellArrows>
            <KarusellArrowButton data-active={canScrollLeft} onClick={() => scrollBy(-1)} aria-label="Forrige">
              {chevronLeft}
            </KarusellArrowButton>
            <KarusellArrowButton data-active={canScrollRight} onClick={() => scrollBy(1)} aria-label="Neste">
              {chevronRight}
            </KarusellArrowButton>
          </KarusellArrows>
        )}
      </Constrainer>
    </VerticalSpacer>
  );
};

function Card({ image, title, text, link, backgroundColor }: KarusellItem & { backgroundColor?: BackgroundColor }) {
  const linkProps = useLinkProps(link);

  return (
    <KarusellCard as={linkProps ? "a" : "div"} {...linkProps} $bg={backgroundColor}>
      {image && (
        <KarusellCardImage
          src={transformImageUrl(image, { width: 400, height: 240, fit: "cover" })}
          alt={image.altText}
          width={400}
          height={240}
        />
      )}
      <KarusellCardBody>
        {title && <KarusellCardTitle>{title}</KarusellCardTitle>}
        {text && <KarusellCardText>{text}</KarusellCardText>}
      </KarusellCardBody>
    </KarusellCard>
  );
}
